import { Fuel } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { totalSobreTudo } from '@/componentes/totais';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarDataHora, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { EtiquetaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { opcaoDaMaquina, type MaquinaDaEscolha } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessao } from '@/modulos/sessao/consultas';

import { comErroDe, conferirTodos, type ErrosDeCampo, marcaDeErro, NENHUM_ERRO, temErro } from './erros-de-campo';
import { explicarFalha, useAutorizarOrdem, useCadastros, useCancelarOrdem, useOrdens } from './consultas';
import type { Ordem } from './tipos';
import { HubDoCombustivel } from './HubDoCombustivel';
import { AcoesNoRodape, JanelaDeAcaoComRodape } from './RodapeDaJanela';

/**
 * Ordem de abastecimento — a AUTORIZAÇÃO, que nunca é a entrega.
 *
 * ## Por que esta tela existe separada do lançamento
 *
 * O documento v6 pede, palavra por palavra, que a ferramenta "separe
 * autorização de abastecimento da quantidade efetivamente entregue", e nomeia o
 * risco de não fazer isso: "autorização ser confundida com combustível
 * efetivamente entregue".
 *
 * A separação é estrutural — são duas tabelas no banco, não duas colunas — e
 * aqui ela é visual: **Autorizado**, **Entregue** e **Saldo** são três colunas,
 * e nenhuma tela do sistema soma as duas primeiras. Quem autoriza faz isso
 * daqui; quem entrega lança na tela de abastecimento e escolhe a ordem.
 *
 * ## Cancelar exige motivo
 *
 * Não é zelo: é `check` do banco. Ordem cancelada sem motivo é a linha que, três
 * meses depois, ninguém sabe explicar — e a conferência de combustível é
 * exatamente o lugar onde alguém vai perguntar.
 */

function situacaoEscrita(ordem: Ordem): string {
  if (ordem.situacao === 'aberta') return 'Aberta';
  if (ordem.situacao === 'atendida') return 'Atendida';
  return 'Cancelada';
}

export function PaginaOrdens() {
  const [autorizando, setAutorizando] = useState(false);
  const consulta = useOrdens();
  const cadastros = useCadastros();
  const cancelar = useCancelarOrdem();
  /* Autorizar e cancelar ordem, cada uma com a sua licença (prova do marco,
     07/10/2026): o Gestor operacional faz as duas; quem só lê, nenhuma. */
  const sessao = useSessao();
  const podeAutorizar = (sessao.data?.permissoes ?? []).includes('ordem_abastecimento.emitir');
  const podeCancelar = (sessao.data?.permissoes ?? []).includes('ordem_abastecimento.cancelar');

  const ordens = consulta.data ?? [];

  const paginaDasOrdens = usePaginacao(ordens);

  const entregueNoPeriodo = ordens.reduce((soma, o) => soma + o.entregueL, 0);

  const colunas: readonly Coluna<Ordem>[] = [
    {
      chave: 'numero',
      rotulo: 'Ordem',
      /* Número e data com hora: nada aqui quebra (p.18). */
      quebra: 'nunca',
      celula: (o) => (
        <>
          <span className="codigo block text-tinta">{formatarInteiro(o.numero)}</span>
          <span className="txt-linha2 block text-tinta-secundaria">{formatarDataHora(o.autorizadaEm)}</span>
        </>
      ),
    },
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      celula: (o) => (
        <>
          <span className="codigo block text-tinta">{o.equipamentoCodigo}</span>
          <TextoDaCelula texto={o.tanque} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      /* Rótulo escrito, nunca só a cor (p.25, regra 2). E sem badge de status:
         as cores de status pertencem à máquina, não à ordem — usá-las aqui
         inventaria um sétimo status de lado numa tela. */
      /* O motivo do cancelamento é texto livre e longo ("Cancelada na tela de
         ordens, com 550 L ainda autorizados"): numa célula que não quebra, ele
         sozinho alargava a tabela inteira. Vai numa linha, com reticências e
         inteiro no `title` — e inteiro no cartão, abaixo de 768. */
      celula: (o) => (
        <>
          <span className="txt-corpo block text-tinta">{situacaoEscrita(o)}</span>
          {o.motivoCancelamento ? (
            <TextoDaCelula texto={o.motivoCancelamento} classe="txt-apoio text-tinta-apagada" linhas={1} />
          ) : null}
        </>
      ),
    },
    {
      chave: 'autorizado',
      rotulo: 'Autorizado',
      alinhamento: 'numero',
      celula: (o) => <Medida valor={formatarInteiro(o.autorizadoL)} unidade="L" />,
      total: (linhas) => (
        <Medida valor={formatarInteiro(linhas.reduce((soma, o) => soma + o.autorizadoL, 0))} unidade="L" />
      ),
    },
    {
      chave: 'entregue',
      rotulo: 'Entregue',
      alinhamento: 'numero',
      celula: (o) => (
        <>
          <Medida valor={formatarInteiro(o.entregueL)} unidade="L" />
          <span className="txt-linha2 block text-tinta-secundaria">
            {o.abastecimentos === 0
              ? 'nenhum abastecimento'
              : `${formatarInteiro(o.abastecimentos)} abastecimentos`}
          </span>
        </>
      ),
      total: (linhas) => (
        <Medida valor={formatarInteiro(linhas.reduce((soma, o) => soma + o.entregueL, 0))} unidade="L" />
      ),
    },
    {
      chave: 'saldo',
      /* "Saldo", como no texto da tela: Autorizado, Entregue e Saldo. Em caixa
         alta, "Saldo autorizado" era o cabeçalho mais largo da tabela e pedia
         161 px para um número de cinco caracteres. */
      rotulo: 'Saldo',
      alinhamento: 'numero',
      celula: (o) => <Medida valor={formatarInteiro(o.saldoL)} unidade="L" />,
    },
  ];

  return (
    <HubDoCombustivel>
      {/* Só o que a faixa do hub não diz (p.17). "Ordens abertas" e o saldo
          autorizado já estão no card de ordens do hub — valor e contexto —, e
          repeti-los aqui dava a mesma resposta duas vezes na tela.
          Tom da marca: entregue não é status, e o verde era o de "Operando"
          (p.09; R02, p.31: o bloco leva o tom do assunto). */}
      <FaixaIndicadores>
        <CardIndicador
          rotulo="Entregue"
          icone={Fuel}
          tom="marca"
          carregando={consulta.isPending}
          valor={consulta.isPending ? '' : formatarInteiro(entregueNoPeriodo)}
          unidade="L"
          contexto="soma do que saiu contra estas ordens"
        />
      </FaixaIndicadores>

      {/* "Autorizar abastecimento" abre em JANELA (pedido do dono, 25/09): o formulário
          fixo ao lado da tabela tinha o próprio laranja, e o laranja da tela
          é o "Lançar abastecimento" do hub. */}
      {podeAutorizar ? (
      <div className="flex justify-end">
        <HistoriaNaMesa codigo="BTN-CMB-07" className="flex">
          <JanelaDeAcaoComRodape
            rotulo="Autorizar abastecimento"
            titulo="Autorizar abastecimento"
            aberta={autorizando}
            aoAbrir={() => {
              setAutorizando(true);
            }}
            aoFechar={() => {
              setAutorizando(false);
            }}
          >
            <FormularioDeOrdem
              aoCancelar={() => {
                setAutorizando(false);
              }}
              equipamentos={cadastros.data?.equipamentos ?? []}
              tanques={cadastros.data?.tanques ?? []}
              obras={cadastros.data?.obras ?? []}
              centrosCusto={cadastros.data?.centrosCusto ?? []}
              carregandoCadastros={cadastros.isPending}
            />
          </JanelaDeAcaoComRodape>
        </HistoriaNaMesa>
      </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-1">
          <Tabela
            rotulo="Ordens de abastecimento"
            colunas={totalSobreTudo(colunas, ordens)}
            linhas={paginaDasOrdens.visiveis}
            rodape={<RodapeTabela {...paginaDasOrdens.rodape} />}
            alturaDupla
            chaveDaLinha={(o) => o.id}
            /* A AÇÃO DA LINHA (p.18): à direita, no hover e no foco — e sempre à
               vista no toque e nos cartões. Era uma coluna "Ação" com um
               "Cancelar ordem" vermelho em toda linha aberta: em lista longa,
               uma pilha de vermelho fora das cinco funções da cor (R02, p.31). */
            acoes={(o) =>
              podeCancelar && o.situacao === 'aberta' ? (
                <BotaoDestrutivo
                  rotulo="Cancelar ordem"
                  pergunta={`Cancelar a ordem ${formatarInteiro(o.numero)}?`}
                  consequencia={`${formatarInteiro(o.saldoL)} L deixam de estar autorizados para ${o.equipamentoCodigo}. O que já foi entregue continua lançado.`}
                  rotuloConfirmar="Cancelar ordem"
                  {...(cancelar.isPending ? { motivoIndisponivel: 'Cancelando a ordem.' } : {})}
                  aoConfirmar={() => {
                    cancelar.mutate({
                      id: o.id,
                      contexto: {
                        alvo: { codigo: `Ordem ${formatarInteiro(o.numero)}`, nome: o.equipamentoCodigo },
                        fatos: [`${formatarInteiro(o.saldoL)} L deixaram de estar autorizados`],
                      },
                      /* O motivo é obrigatório no banco. Enquanto a tela não tem
                         campo para ele dentro da confirmação, ele é escrito aqui e
                         diz a verdade: quem cancelou foi a tela, por decisão de quem
                         estava olhando a lista. Campo livre na confirmação está
                         registrado como pendência. */
                      motivo: `Cancelada na tela de ordens, com ${formatarInteiro(o.saldoL)} L ainda autorizados`,
                    });
                  }}
                />
              ) : null
            }
            carregando={consulta.isPending}
            rotuloDoTotal="Soma"
            erro={
              consulta.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar as ordens"
                  descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void consulta.refetch();
                      }}
                    >
                      Carregar as ordens
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              <EstadoVazio
                titulo="Nenhuma ordem autorizada"
                descricao={
                  podeAutorizar
                    ? 'Autorize a primeira ordem para separar o que foi liberado do que foi entregue.'
                    : 'Quem autoriza a ordem é o perfil Gestor operacional. Ela aparece aqui depois de autorizada.'
                }
                acao={
                  podeAutorizar ? (
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        setAutorizando(true);
                      }}
                    >
                      Autorizar abastecimento
                    </Botao>
                  ) : undefined
                }
              />
            }
          />
        </div>

      </div>
    </HubDoCombustivel>
  );
}

function FormularioDeOrdem({
  equipamentos,
  tanques,
  obras,
  centrosCusto = [],
  carregandoCadastros,
  aoCancelar,
}: {
  readonly aoCancelar: () => void;
  readonly equipamentos: readonly (MaquinaDaEscolha & {
    readonly obraId: string | null;
    /** O tanque DA MÁQUINA: a ajuda da quantidade diz quanto cabe nela. */
    readonly capacidadeTanqueL?: number | null;
  })[];
  readonly tanques: readonly {
    readonly id: string;
    readonly nome: string;
    readonly padrao: boolean;
    readonly produto?: string;
    readonly saldoCalculadoL?: number;
  }[];
  readonly obras: readonly { readonly id: string; readonly nome: string; readonly centroCustoId: string | null }[];
  /** Os centros de custo, para a etiqueta dizer o nome do centro que a ordem leva. */
  readonly centrosCusto?: readonly { readonly id: string; readonly nome: string }[];
  /** As listas ainda não chegaram: os dois controles são esqueleto, e não listas vazias (p.32). */
  readonly carregandoCadastros: boolean;
}) {
  const base = useId();
  const autorizar = useAutorizarOrdem();

  const [equipamento, setEquipamento] = useState('');
  const [tanque, setTanque] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  const tanqueEfetivo = tanque || (tanques.find((t) => t.padrao)?.id ?? tanques[0]?.id ?? '');
  /* A máquina com a foto da frota (pedido do dono, 20/10/2026), como no
     lançamento. */
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  /* O QUE A ORDEM LEVA SEM PERGUNTAR (31/10/2026): a obra e o centro de custo
     da máquina. Iam no envio calados; agora aparecem em etiqueta, para quem
     autoriza conferir antes do botão. */
  const maquina = equipamentos.find((e) => e.id === equipamento) ?? null;
  const obraDaMaquina = obras.find((o) => o.id === maquina?.obraId) ?? null;
  const centroDaObra = centrosCusto.find((c) => c.id === obraDaMaquina?.centroCustoId) ?? null;

  /* A mesma regra no `onBlur` e no envio (p.16): o erro aparece debaixo do
     campo ao sair dele, e o envio mostra todos de uma vez. */
  function mensagemDe(campo: string): string | null {
    if (campo === 'equipamento') return equipamento ? null : 'Escolha o equipamento que vai ser abastecido.';
    if (campo === 'tanque') return tanqueEfetivo ? null : 'Escolha de qual tanque o combustível vai sair.';
    if (campo === 'quantidade') {
      const valor = interpretarNumero(quantidade);
      return valor === null || valor <= 0 ? 'Informe quantos litros estão sendo autorizados.' : null;
    }
    return null;
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);

    const litros = interpretarNumero(quantidade);
    const conferidos = conferirTodos(['equipamento', 'tanque', 'quantidade'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || litros === null) return;

    const escolhido = equipamentos.find((e) => e.id === equipamento) ?? null;
    const obra = obras.find((o) => o.id === escolhido?.obraId) ?? null;

    autorizar.mutate(
      {
        equipamentoId: equipamento,
        tanqueId: tanqueEfetivo,
        quantidadeAutorizadaL: litros,
        ...(obra ? { obraId: obra.id } : {}),
        ...(obra?.centroCustoId ? { centroCustoId: obra.centroCustoId } : {}),
        motivo: 'autorização de abastecimento pela tela',
        contexto: {
          ...(escolhido ? { alvo: { codigo: escolhido.codigo } } : {}),
          fatos: [`do ${tanques.find((t) => t.id === tanqueEfetivo)?.nome ?? 'tanque escolhido'}`],
        },
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
  }

  const falha = autorizar.isError ? explicarFalha(autorizar.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {falha.erro}. {falha.comoResolver}
        </p>
      ) : null}
      <p className="campo-linha txt-apoio text-tinta-secundaria">
        A ordem é a autorização. Ela não faz combustível sair do tanque: quem faz isso é o lançamento do
        abastecimento.
      </p>

      <Campo id={`${base}-equipamento`} rotulo="Equipamento" erro={erros.equipamento ?? null}>
        {carregandoCadastros ? (
          <ControleCarregando oQue="Carregando a frota" />
        ) : (
          /* A máquina com o código interno e a placa em etiqueta (pedido do
             dono, 31/10/2026), no seletor da mesa (p.23, p.31). */
          <ListaDeEscolha
            id={`${base}-equipamento`}
            rotulo="Equipamento"
            valor={equipamento}
            aoMudar={(valor) => {
              setEquipamento(valor);
              setErros((atual) => comErroDe(atual, 'equipamento', null));
            }}
            aoSair={() => {
              conferir('equipamento');
            }}
            rotuloVazio="Escolha o equipamento"
            opcoes={comVisualDaFrota(equipamentos.map((e) => opcaoDaMaquina(e)))}
            idDoErro={erros.equipamento ? `${base}-equipamento-erro` : undefined}
            adesivo
          />
        )}
      </Campo>

      <Campo
        id={`${base}-tanque`}
        rotulo="Tanque"
        preenchido={tanque === '' && tanqueEfetivo !== ''}
        ajuda={
          tanque === '' && tanqueEfetivo !== ''
            ? 'O tanque padrão. O abastecimento desta ordem tem que sair dele: o banco recusa outro.'
            : 'O abastecimento lançado contra esta ordem tem que sair deste tanque: o banco recusa outro.'
        }
        erro={erros.tanque ?? null}
      >
        {carregandoCadastros ? (
          <ControleCarregando oQue="Carregando os tanques" />
        ) : (
          /* O seletor do sistema, com o produto em etiqueta e o volume
             embaixo; com um tanque só, ele já vem escolhido. */
          <ListaDeEscolha
            id={`${base}-tanque`}
            rotulo="Tanque"
            valor={tanqueEfetivo}
            aoMudar={(valor) => {
              setTanque(valor);
              setErros((atual) => comErroDe(atual, 'tanque', null));
            }}
            aoSair={() => {
              conferir('tanque');
            }}
            opcoes={tanques.map((t) => ({
              valor: t.id,
              rotulo: t.nome,
              ...(t.produto ? { etiquetas: [{ texto: t.produto, tom: 'neutro' as const, titulo: 'Produto' }] } : {}),
              ...(t.saldoCalculadoL === undefined ? {} : { detalhe: `saldo ${formatarInteiro(t.saldoCalculadoL)} L` }),
            }))}
            idDoErro={erros.tanque ? `${base}-tanque-erro` : undefined}
          />
        )}
      </Campo>

      <Campo
        id={`${base}-quantidade`}
        rotulo="Quantidade autorizada"
        ajuda={`${
          maquina?.capacidadeTanqueL ? `O tanque de ${maquina.codigo} comporta ${formatarInteiro(maquina.capacidadeTanqueL)} L. ` : ''
        }É o teto da autorização: o entregue pode ser menos, ou mais, e aí o saldo fica negativo e à vista.`}
        erro={erros.quantidade ?? null}
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-quantidade`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={quantidade}
            placeholder="200"
            className="controle w-full"
            onChange={(evento) => {
              setQuantidade(evento.target.value);
              setErros((atual) => comErroDe(atual, 'quantidade', null));
            }}
            onBlur={() => {
              conferir('quantidade');
            }}
            {...marcaDeErro(erros, 'quantidade', `${base}-quantidade`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
        </div>
      </Campo>

      {maquina ? (
        /* Sem "Ajustar": a ordem leva o que a alocação da máquina diz, e
           quem muda isso é a alocação. Aqui é só para conferir. */
        <div className="campo-linha flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <span className="txt-rotulo-campo shrink-0">Vai para</span>
          <EtiquetaDoPreenchido rotulo="Obra" valor={obraDaMaquina?.nome ?? 'Sem obra'} vazia={obraDaMaquina === null} />
          <EtiquetaDoPreenchido rotulo="Centro de custo" valor={centroDaObra?.nome ?? 'Sem centro'} vazia={centroDaObra === null} />
        </div>
      ) : null}

      {pronto ? (
        <p role="status" className="campo-linha txt-apoio rounded-controle border border-borda bg-rebaixada px-4 py-2 text-tinta">
          {pronto}
        </p>
      ) : null}

      {/* Dentro da janela, a ação dela é a laranja; "Cancelar" é terciário à
          esquerda (p.15), o mesmo desenho em todas as janelas do módulo, no
          rodapé fixo da janela (R01, p.29). */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao nivel="primario" type="submit" form={`${base}-formulario`} disabled={autorizar.isPending}>
          Autorizar abastecimento
        </Botao>
      </AcoesNoRodape>
    </form>
  );
}
